import {ACTION_TYPES} from '@/consts/loyalty/actionTypes';
import {JOURNEY_STATUS_TYPES} from '@/consts/loyalty/journeyStatusTypes';
import amplitudeLoyaltyTracking from '@/services/analytics/amplitude/amplitudeLoyaltyTracking';
import googleAnalyticsService from '@/services/analytics/ga/googleAnalyticsService';
import {isBadgeAchieved} from '@/services/loyalty/badgeUtils';
import {setJourneyBadgePending, setJourneyLevelTaskPending} from '@/state/slices/loyaltySlice';
import {dispatch} from '@/state/store';

export const isJourneyCompleted = ({memberJourneyStatus} = {}) => {
    return memberJourneyStatus === JOURNEY_STATUS_TYPES.COMPLETED;
};

export const isJourneyActionCompleted = (levelAction) => {
    return levelAction?.memberJourneyActionStatus === JOURNEY_STATUS_TYPES.COMPLETED;
};

export const isJourneyActionNotStarted = (levelAction) => {
    return levelAction?.memberJourneyActionStatus === JOURNEY_STATUS_TYPES.NOT_STARTED;
};

export const isJourneyLevelInProgress = ({actionsLeft, journeyActions} = {}) => {
    return actionsLeft > 0 && actionsLeft <= journeyActions?.length;
};

export const isJourneyLevelCompleted = ({actionsLeft} = {}) => {
    return actionsLeft === 0;
};

export const isJourneyInProgress = ({memberJourneyStatus} = {}) => {
    return memberJourneyStatus === JOURNEY_STATUS_TYPES.IN_PROGRESS;
};

export const isBadgeAction = ({actionType} = {}) => {
    return actionType === ACTION_TYPES.BADGE;
};

export const getEarnedBadgeIds = (memberJourneys) => {
    const badgeIds = new Set();

    for (const {journeyLevels} of memberJourneys || []) {
        for (const {journeyBadge} of journeyLevels || []) {
            if (isBadgeAchieved(journeyBadge)) {
                badgeIds.add(journeyBadge.badgeId);
            }
        }
    }

    return badgeIds;
};

export const withResolvedBadgeActions = (journey, earnedBadgeIds) => {
    if (!getBadgeActionLevels(journey).length) {
        return journey;
    }

    const journeyLevels = journey.journeyLevels.map((level) => {
        const actions = level.journeyActions;

        if (!actions?.some(isBadgeAction)) {
            return level;
        }

        const earnedActions = actions.filter((action) => isBadgeAction(action) && earnedBadgeIds.has(action.badgeId));

        return {...level, actionsLeft: actions.length - earnedActions.length};
    });

    return {...journey, journeyLevels};
};

const getBadgeActionLevels = ({journeyLevels} = {}) => {
    return journeyLevels?.filter(({journeyActions}) => journeyActions?.some(isBadgeAction)) || [];
};

export const hasStartedBadgeActions = (journey) => {
    return getBadgeActionLevels(journey).some(({journeyActions, actionsLeft}) => actionsLeft < journeyActions.length);
};

export const hasCompletedBadgeActions = (journey) => {
    const badgeActionLevels = getBadgeActionLevels(journey);

    return (
        badgeActionLevels.length > 0 &&
        badgeActionLevels.length === journey.journeyLevels.length &&
        badgeActionLevels.every(({actionsLeft}) => actionsLeft === 0)
    );
};

export const getJourneyInfo = ({journeyLevels, memberJourneyStatus: isMemberJourney} = {}) => {
    const levels = journeyLevels || [];
    let actionsLeft = 0;
    let actionsCount = 0;
    let currentLevel = null;
    let completedLevels = 0;

    for (const level of levels) {
        const actions = level.journeyActions;

        const levelActionsCount = actions?.length || 0;

        actionsCount += levelActionsCount;
        actionsLeft += isNaN(level.actionsLeft) ? actions?.length : level.actionsLeft;

        if (isJourneyLevelCompleted(level)) {
            completedLevels += 1;
            continue;
        }
        if (!isJourneyLevelInProgress(level)) {
            continue;
        }
        if (!currentLevel) {
            currentLevel = level;
        }
    }

    const isSingleLevel = levels.length === 1;

    // If no level is in progress, set the badge to the last level's badge
    currentLevel ??= levels[isMemberJourney ? levels.length - 1 : 0] || {};
    const currentLevelBadge = currentLevel?.journeyBadge || null;

    const progressCompleted = isSingleLevel ? actionsCount - actionsLeft : completedLevels;
    const progressTotal = isSingleLevel ? actionsCount : levels.length;

    return {
        actionsLeft,
        actionsCount,
        currentLevelBadge,
        currentLevel,
        progressCompleted,
        progressTotal,
        isSingleLevel,
    };
};

export const getJourneyAction = (journeys, {actionIndex, levelIndex, journeyId} = {}) => {
    const level = journeys?.find((journey) => journey.journeyId === journeyId)?.journeyLevels[levelIndex];

    if (!level) return null;
    const action = level.journeyActions?.[actionIndex];

    if (!action) return null;

    return {...action, journeyBadge: level.journeyBadge};
};

export const getJourneyLevelProgress = (journeyLevel = {}) => {
    const journeyActions = journeyLevel.journeyActions;
    const progressCount = journeyActions?.filter(isJourneyActionCompleted)?.length || 0;
    const totalCount = journeyActions?.length || 0;
    const lastNotCompletedAction = journeyActions?.find((action) => !isJourneyActionCompleted(action));

    return {progressCount, totalCount, lastNotCompletedAction, isCompleted: progressCount === totalCount};
};

export const trackLoyaltyClick = (badgeId) => {
    amplitudeLoyaltyTracking.trackBadgeDetailsClick(badgeId);
    googleAnalyticsService.trackBadgeDetailsClick(badgeId);
    dispatch(setJourneyBadgePending(true));
    dispatch(setJourneyLevelTaskPending(true));
};
